import {forwardRef, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import utils from '@eitje/utils'
import {makeCnVariants, childrenWithClonedProps} from '../../helpers'
import Portal from '../portal'
import {ClickOutsideManager} from './click_outside_manager'
import {Layout} from '../layout'
import {useGetPopoutBody} from './popout_content'
import {useHover, useDelayedHover, useIsHovered} from './hooks/use_hover'
import {useConditionalInterval} from './hooks/use_conditional_interval'
import {usePopper} from './hooks/use_popper'
import {useHideShow} from './hooks/use_hide_show'
import './styles/index.less'
import './styles/tooltip.less'

const mapDelimitedString = (value, fn, delimiter = ' ') => value.split(delimiter).map(fn).join(delimiter)

// DEPRECATED - use the Popout component instead
export const PopoutCard = forwardRef((props, ref) => {
	const {
		children,
		className,
		trigger = props.onConfirm ? 'click' : 'hover',
		shouldAlwaysRender,
		hidden,
		inlineTrigger,
		disabled,
		onConfirm,
		title,
		onVisibleChange = _.noop,
		layoutProps = {},
		style,
		wrapperLayoutProps,
		delayedHovering,
		extraHoverElementSelector,
		displayContents,
		onClose,
	} = props
	const initialMaxHeight = props.maxHeight || 1000
	const [maxHeight, setMaxHeight] = useState(initialMaxHeight)
	const [internalOpen, setInternalOpen] = useState()
	const internalRef = useRef()
	ref = ref || internalRef
	const open = _.has(props, 'open') ? props.open : internalOpen
	const containerRef = useRef()
	const hoverState = useHover()
	const {hoverActions, setHovering} = hoverState
	let {isHovering} = hoverState
	const delayedIsHovering = useDelayedHover(isHovering)
	const hoverEnabled = trigger === 'hover'
	const isTooltip = title && !props.body && !onConfirm
	const isPopoutHoverable = !isTooltip || !_.isString(title)

	const handleClick =
		hidden || hoverEnabled
			? e => {
					props.onClick?.(e) // should actually also put this in the other condition, but let's keep it minimal for now to see the impact
				}
			: e => {
					e.preventDefault()
					e.stopPropagation()
					setInternalOpen(!open)
					if (!open) setMaxHeight(props.maxHeight)
				}

	isHovering = delayedHovering ? delayedIsHovering : isHovering
	const hasDocument = typeof document !== 'undefined'
	const extraHoverElement = hasDocument && document.querySelector(extraHoverElementSelector)
	const extraElementIsHovered = useIsHovered(extraHoverElement)
	const hasContent = title || props.body
	const showPopout = !disabled && !hidden && hasContent && (open || (hoverEnabled && isHovering) || extraElementIsHovered)

	useHideShow({
		ref,
		setIsOpen: setInternalOpen,
		isOpen: showPopout,
		setHovering,
	})

	// To fix the issue with tooltips not closing due to onMouseLeave not being triggered correctly
	// Once openend, we check each second if the user is still hovering and if not we programatically close it by setting isHovering to false
	useConditionalInterval(
		showPopout,
		() => {
			const isPopoutCurrentlyHovered = Array.from(document.querySelectorAll(':hover')).includes(containerRef.current)
			if (!isPopoutCurrentlyHovered && isTooltip) {
				setHovering(false)
			}
		},
		1000,
	)

	useEffect(() => {
		if (!showPopout) setMaxHeight(initialMaxHeight)
		onVisibleChange(showPopout)
	}, [showPopout])

	const popoutInModal = hasDocument && document.querySelector('.ant-modal-root')
	const boundaryElement = !popoutInModal && hasDocument && document.querySelector('.popout-boundary')
	const popoutInBoundary = boundaryElement?.contains?.(containerRef.current)

	const {wrapperProps, styles, referenceElement, popperElement, arrowElement} = usePopper({
		...props,
		boundaryElement: popoutInBoundary && boundaryElement,
		isTooltip,
		showPopout,
		containerRef,
		maxHeight,
		setMaxHeight,
	})

	const containerClassNames = utils.makeCns(
		'popout-card',
		inlineTrigger && 'popout-card-inline',
		displayContents && 'display-contents',
		className,
	)

	const popoutCnVariants = makeCnVariants(
		'popout-wrapper',
		showPopout && 'visible',
		isTooltip && 'is-tooltip',
		isPopoutHoverable && 'is-hoverable',
	)
	const popoutClassNames = utils.makeCns(popoutCnVariants, className && mapDelimitedString(className, c => `${c}-popout`))

	const triggerClassNames = utils.makeCns(
		'popout-card-trigger',
		className && mapDelimitedString(className, c => `${c}-trigger`),
		displayContents && 'display-contents',
	)
	const {header, body, footer} = useGetPopoutBody({
		...props,
		setHovering,
		isTooltip,
		ref,
		maxHeight,
	})

	return (
		<Layout ref={containerRef} {...hoverActions} className={containerClassNames} {...wrapperLayoutProps} style={style}>
			<Layout
				ref={referenceElement}
				onClick={handleClick}
				className={triggerClassNames}
				hasHover={!hidden}
				disabled={disabled}
				width="100%"
				{...layoutProps}
			>
				{childrenWithClonedProps({
					children,
					props: {disabled},
					condition: disabled,
				})}
			</Layout>

			{(showPopout || shouldAlwaysRender) && (
				<Portal>
					<div ref={popperElement} className={popoutClassNames} {...wrapperProps}>
						{!hoverEnabled && (
							<ClickOutsideManager
								ref={containerRef}
								onClick={() => {
									onClose?.()
									setInternalOpen(false)
								}}
							/>
						)}
						{isPopoutHoverable && hoverEnabled && <div className="hover-zone" />}
						<Layout className="popout-content" direction="vertical" gap={0}>
							{header}
							{body}
							{footer}
						</Layout>
						{isTooltip && <div className="tooltip-arrow" ref={arrowElement} style={styles.arrow} />}
					</div>
				</Portal>
			)}
		</Layout>
	)
})

// Placement
//  auto
//  auto-start
//  auto-end
//  top
//  top-start
//  top-end
//  bottom
//  bottom-start
//  bottom-end
//  right
//  right-start
//  right-end
//  left
//  left-start
//  left-end
